/**
 * Mobile-OK 콜백 페이지
 * 백엔드 /func/mobileok/callback에서 리다이렉트되어 mokToken을 처리
 *
 * 흐름:
 * 1. SDK → 백엔드 /func/mobileok/callback (POST mokToken)
 * 2. 백엔드 → 프론트 /auth/mobileok/callback?mokToken=xxx (GET 리다이렉트)
 * 3. 이 페이지 → /func/mobileok/result API 호출 (mokToken 복호화)
 * 4. opener.postMessage로 결과 전달 → 창 닫기
 */

import { useEffect, useState } from 'react';
import { useMobileOKCallback } from '../hooks/useMobileOK';

const MobileOKCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('본인인증 처리 중...');

  const { result, isLoading, error } = useMobileOKCallback({
    onSuccess: (data) => {
      console.log('[MobileOKCallback] 인증 성공:', data);
      setStatus('success');
      setMessage('본인인증이 완료되었습니다.');

      // opener (부모 창)에 결과 전달
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'MOBILEOK_CALLBACK',
            success: 'true',
            message: '인증 성공',
            data: {
              name: data.name,
              ci: data.ci,
              di: data.di,
              birthDate: data.birthDate,
              gender: data.gender,
              mobileNo: data.mobileNo,
              mobileCo: data.mobileCo,
            },
          },
          window.location.origin
        );

        // 잠시 후 창 닫기
        setTimeout(() => {
          window.close();
        }, 1500);
      }
    },
    onError: (err) => {
      console.error('[MobileOKCallback] 인증 실패:', err);
      setStatus('error');
      setMessage(err.message || '본인인증에 실패했습니다.');

      // opener에 에러 전달
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'MOBILEOK_CALLBACK',
            success: 'false',
            message: err.message || '본인인증에 실패했습니다.',
          },
          window.location.origin
        );
      }
    },
  });

  useEffect(() => {
    // URL 파라미터 디버깅
    const urlParams = new URLSearchParams(window.location.search);
    console.log('[MobileOKCallback] URL params:', {
      mokToken: urlParams.get('mokToken'),
      resultCode: urlParams.get('resultCode'),
      resultMsg: urlParams.get('resultMsg'),
    });
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'sans-serif',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div
        style={{
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center',
        }}
      >
        {status === 'loading' && (
          <>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid #e0e0e0',
                borderTop: '4px solid #4a90d9',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px',
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === 'success' && (
          <div
            style={{
              width: '50px',
              height: '50px',
              backgroundColor: '#4CAF50',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white',
              fontSize: '24px',
            }}
          >
            ✓
          </div>
        )}

        {status === 'error' && (
          <div
            style={{
              width: '50px',
              height: '50px',
              backgroundColor: '#f44336',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white',
              fontSize: '24px',
            }}
          >
            ✕
          </div>
        )}

        <p style={{ fontSize: '16px', color: '#333', margin: 0 }}>{message}</p>

        {status === 'success' && result && (
          <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
            {result.name}님 인증 완료
          </p>
        )}

        {status !== 'loading' && (
          <button
            onClick={() => window.close()}
            style={{
              marginTop: '20px',
              padding: '10px 24px',
              backgroundColor: '#4a90d9',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            창 닫기
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileOKCallback;